import { FC, useEffect, useState, useMemo } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  ReportGroupBy,
  ReportPeriod,
  TReportOldAffiliateCommissionsData,
} from '@src/types/reports';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { buildAffiliateCommissionsPayload } from '@src/utils/buildCommonPayload';
import {
  exportOldAffiliateCommissions,
  getOldAffiliateCommissions,
} from '@src/services/reports';
import { toast } from 'react-toastify';
import { downloadFile } from '@src/lib/utils';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';
import { useAuthStore } from '@src/stores/authStore';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: TReportOldAffiliateCommissionsData & { report_date: string };
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col items-center min-w-[135px] gap-y-1"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#B6D72C] text-xl font-semibold">
          ${payload[0]?.value.toLocaleString()}
        </p>
        <p className="text-[#999999] text-[10px]">
          {payload[0]?.payload?.report_date}
        </p>
        <p className="text-white text-xs">
          Commissions: ${payload[0]?.value.toLocaleString()}
        </p>
      </div>
    );
  }

  return null;
};

export const OldAffiliateCommissions: FC = () => {
  const { auth } = useAuthStore();
  const [data, setData] = useState<TReportOldAffiliateCommissionsData[]>([]);

  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.THIS_MONTH,
    defaultGroupBy: ReportGroupBy.WEEK,
    enableComparisons: true,
  });

  // Transform data for chart display
  const chartData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      commission_display: item.commission,
    }));
  }, [data]);

  const { columns, tableData } =
    useReportTableColumns<TReportOldAffiliateCommissionsData>({
      data,
      getAccessField: (item) => item.report_date,
      getMainDataField: (item) => item.commission || 0,
      customColumns: [
        {
          title: 'COMMISSIONS, USD',
          className: 'text-right',
          render: (item) => (
            <p className="text-white">
              ${item?.commission?.toLocaleString() ?? 0}
            </p>
          ),
        },
      ],
      getPreviousPeriodField: (item) => item.previous_period_commission,
      getPreviousYearField: (item) => item.previous_year_commission,
      includeTotalsRow: true,
    });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildAffiliateCommissionsPayload(filters);
      const response = await getOldAffiliateCommissions(payload);

      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Error generating report. Please try again.');
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const payload = buildAffiliateCommissionsPayload(filters);
      const response = await exportOldAffiliateCommissions(payload);

      downloadFile(response.data, 'old-affiliate-commissions');
      toast.success('Report exported successfully!');
    } catch (error) {
      toast.error('Error exporting report. Please try again.');
      console.error('Error exporting report:', error);
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Old Affiliate Commissions Report
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="report_date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="line" wrapperStyle={{ fontSize: '14px' }} />
              <Line
                activeDot={{ r: 6 }}
                dataKey="commission_display"
                dot={{ fill: '#B6D72C', r: 4 }}
                name="Commissions"
                stroke="#B6D72C"
                strokeWidth={2}
                type="monotone"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          {canAccess(
            UserRolePermission.EXPORT_REPORTS,
            auth?.user?.permissions
          ) && (
            <div className="flex justify-end">
              <Button
                className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                variant="outline"
                onClick={handleExportReport}
              >
                Download Excel
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
